import { Modal } from "../../UI/Modal/Modal.tsx";
import { Dialog } from "@headlessui/react";
import { OutlinedButton } from "@normasteaching/norma-ui";
import { useMessageController } from "../../UI/useMessageController.ts";
import { SuccessMessage } from "../../UI/Success/SuccessMessage.tsx";
import copy from "copy-to-clipboard";
import {
  CouponType,
  NormasTeachingCouponDTO,
  ApplicableProduct,
} from "../../ProductService/NormasTeachingCoupon/useNormasTeachingCouponById.ts";
import { useAllNormasTeachingProducts } from "../../ProductService/NormasTeachingProduct/useAllNormasTeachingProducts.ts";
import { useAllNormasTeachingBundles } from "../../ProductService/NormasTeachingBundle/useAllNormasTeachingBundles.ts";
import { Package, Layers } from "lucide-react";
import { ExpandableCard } from "../../UI/ExpandableCard.tsx";
import { NormasTeachingItemType } from "../../Stripe/StripeOrderDTO.ts";

function getCouponStatus(coupon: NormasTeachingCouponDTO): {
  isActive: boolean;
  reason: string;
} {
  const now = new Date();
  if (!coupon.isActive) {
    return { isActive: false, reason: "Disattivato manualmente" };
  } else if (coupon.validFrom && now < new Date(coupon.validFrom)) {
    return { isActive: false, reason: "Non ancora valido" };
  } else if (coupon.validTo && now > new Date(coupon.validTo)) {
    return { isActive: false, reason: "Scaduto" };
  }
  return { isActive: true, reason: "Attivo" };
}

function formatDate(dateString?: string): string {
  if (!dateString) return "-";
  return new Date(dateString).toLocaleDateString("it-IT", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function formatCouponValue(value: {
  amount: number;
  couponType: CouponType;
}): string {
  if (value.couponType === CouponType.Percentage) {
    return `${value.amount}%`;
  }
  return `€${(value.amount / 100).toFixed(2)}`;
}

function CouponDetailsModal({
  coupon,
  onClose,
}: {
  coupon: NormasTeachingCouponDTO;
  onClose: () => void;
}) {
  const { showMessage, message, open } = useMessageController();
  const status = getCouponStatus(coupon);

  const { products } = useAllNormasTeachingProducts();
  const { bundles } = useAllNormasTeachingBundles();

  const getItemName = (item: ApplicableProduct): string => {
    if (item.itemType === NormasTeachingItemType.Product) {
      const product = products.find((p) => p._id === item.id);
      return product
        ? `${product.productName} - ${product.variantProductName}`
        : "Prodotto sconosciuto";
    } else {
      const bundle = bundles.find((b) => b._id === item.id);
      return bundle
        ? `${bundle.bundleName} - ${bundle.variantBundleName}`
        : "Bundle sconosciuto";
    }
  };

  const hasApplicableProducts =
    coupon.applicableProducts && coupon.applicableProducts.length > 0;

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Dettagli Coupon"
      overridePanel={({ title, children }) => (
        <Dialog.Panel className="flex-1 md:flex-auto md:max-w-2xl transform overflow-hidden md:rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
          <Dialog.Title
            as="h3"
            className="text-lg font-medium leading-6 text-gray-900"
          >
            {title}
          </Dialog.Title>
          <div className="mt-4">{children}</div>
        </Dialog.Panel>
      )}
    >
      <SuccessMessage open={open} message={message} />
      <div className="space-y-6 max-h-[70vh] overflow-y-auto max-w-2xl">
        {/* Informazioni generali */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Informazioni Generali
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Coupon ID:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600"
                onClick={() => {
                  copy(coupon._id);
                  showMessage({
                    message: "ID del coupon copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {coupon._id}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Codice Coupon:</span>
              <p
                className="text-gray-600 cursor-pointer hover:text-blue-600 uppercase"
                onClick={() => {
                  copy(coupon.couponCode);
                  showMessage({
                    message: "Codice coupon copiato negli appunti!",
                    timeoutMs: 4000,
                  });
                }}
              >
                {coupon.couponCode}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Tipologia:</span>
              <p className="text-gray-600">
                {coupon.value.couponType === CouponType.Percentage
                  ? "Percentuale"
                  : "Fisso"}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valore:</span>
              <p className="text-gray-600 font-semibold">
                {formatCouponValue(coupon.value)}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">
                Utilizzi Massimi:
              </span>
              <p className="text-gray-600">
                {coupon.isUnlimitedUses ? (
                  <span className="text-green-600 font-semibold">
                    Illimitati
                  </span>
                ) : (
                  coupon.maxNumberOfUses
                )}
              </p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Stato:</span>
              <p
                className={`${
                  status.isActive ? "text-green-600" : "text-red-600"
                } font-semibold`}
              >
                {status.reason}
              </p>
            </div>
          </div>
        </div>

        {/* Validità */}
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-gray-900 border-b pb-2">
            Periodo di Validità
          </h3>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <span className="font-medium text-gray-700">Valido da:</span>
              <p className="text-gray-600">{formatDate(coupon.validFrom)}</p>
            </div>
            <div>
              <span className="font-medium text-gray-700">Valido fino a:</span>
              <p className="text-gray-600">{formatDate(coupon.validTo)}</p>
            </div>
          </div>
        </div>

        {/* Prodotti Applicabili */}
        <ExpandableCard title="Prodotti Applicabili" defaultExpanded={true}>
          {hasApplicableProducts ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-600 mb-3">
                Questo coupon è applicabile solo ai seguenti prodotti/bundle:
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {coupon.applicableProducts!.map((item) => (
                  <div
                    key={`${item.itemType}-${item.id}`}
                    className="flex items-center justify-between p-2 bg-gray-50 rounded"
                  >
                    <div className="flex items-center gap-2">
                      {item.itemType === NormasTeachingItemType.Product ? (
                        <Package size={16} className="text-blue-500" />
                      ) : (
                        <Layers size={16} className="text-purple-500" />
                      )}
                      <span
                        className="text-sm cursor-pointer hover:text-blue-600"
                        onClick={() => {
                          copy(item.id);
                          showMessage({
                            message: "ID copiato negli appunti!",
                            timeoutMs: 4000,
                          });
                        }}
                      >
                        {getItemName(item)}
                      </span>
                      <span
                        className={`text-xs px-2 py-0.5 rounded ${
                          item.itemType === NormasTeachingItemType.Product
                            ? "bg-blue-100 text-blue-700"
                            : "bg-purple-100 text-purple-700"
                        }`}
                      >
                        {item.itemType === NormasTeachingItemType.Product
                          ? "Prodotto"
                          : "Bundle"}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="bg-green-50 p-3 rounded-lg">
              <p className="text-sm text-green-800 font-medium">
                ✓ Questo coupon è applicabile a tutti i prodotti e bundle
              </p>
            </div>
          )}
        </ExpandableCard>

        {/* Pulsante chiudi */}
        <div className="pt-4 border-t">
          <OutlinedButton onClick={onClose} label="Chiudi" />
        </div>
      </div>
    </Modal>
  );
}

export default CouponDetailsModal;
